import type { EnumUnion } from '@dresscode-frontend/utility-types';
import { match } from 'ts-pattern';
import type { BadgeVariant } from '@/components/Badge';
import { type IssueListItem, type IssuePriority, IssuePriority as IPri, type IssueSeverity, IssueSeverity as ISev, type IssueStatus, IssueStatus as IS } from './type';
type IssueNameColumn = {
  id: string;
  name: string;
};
type IssueAssignee = {
  personId: string;
  name: string;
};
type IssueReporter = {
  personId: string;
  name: string;
};
type IssueRelease = {
  id: string;
  productName: string;
  releaseName: string;
};
type IssueTargetNode = {
  nodeId: string;
  nodeName: string;
};
type IssueRelatedEntity = {
  id: string;
  name: string;
};

/**
 * プロダクト・発生場所列。release と targetNodes を組合せて 1 セルで表示する。
 *
 * release は開発スコープ未割当（productionId=null）の Issue では null になる
 * （BE: Item.production が null）。セル側で「未割当」表示にフォールバックする。
 */
type IssueReleaseAndTargetNodes = {
  release: IssueRelease | null;
  targetNodes: IssueTargetNode[];
};
export const IssueTableColumns = {
  ISSUE: 'ISSUE',
  RELEASE_AND_TARGET_NODES: 'RELEASE_AND_TARGET_NODES',
  STATUS: 'STATUS',
  STATUS_LABEL: 'STATUS_LABEL',
  SEVERITY: 'SEVERITY',
  SEVERITY_LABEL: 'SEVERITY_LABEL',
  ENVIRONMENT: 'ENVIRONMENT',
  DETECTION_PATH: 'DETECTION_PATH',
  ASSIGNEES: 'ASSIGNEES',
  REPORTER: 'REPORTER',
  CREATED_AT: 'CREATED_AT'
} as const;
export type IssueTableColumns = EnumUnion<typeof IssueTableColumns>;
export type IssueTableRowData = {
  [IssueTableColumns.ISSUE]: IssueNameColumn;
  [IssueTableColumns.RELEASE_AND_TARGET_NODES]: IssueReleaseAndTargetNodes;
  [IssueTableColumns.STATUS]: IssueStatus | undefined;
  [IssueTableColumns.STATUS_LABEL]: string | undefined;
  [IssueTableColumns.SEVERITY]: IssueSeverity | undefined;
  [IssueTableColumns.SEVERITY_LABEL]: string | undefined;
  [IssueTableColumns.ENVIRONMENT]: IssueRelatedEntity | null;
  [IssueTableColumns.DETECTION_PATH]: IssueRelatedEntity | null;
  [IssueTableColumns.ASSIGNEES]: IssueAssignee[];
  [IssueTableColumns.REPORTER]: IssueReporter;
  [IssueTableColumns.CREATED_AT]: string | undefined;
};
export function getIssueStatusBadgeVariant(status: IssueStatus): BadgeVariant {
  return match<IssueStatus, BadgeVariant>(status).with(IS.UNTOUCHED, () => 'errorMuted').with(IS.IN_PROGRESS, () => 'primaryMuted').with(IS.BACKLOG, () => 'neutralMuted').with(IS.WAITING_REPRODUCTION, () => 'mutedWarning').with(IS.RESOLVED, () => 'mutedSuccess').with(IS.DISMISSED, () => 'neutralMuted').exhaustive();
}
export function getIssueSeverityBadgeVariant(severity: IssueSeverity): BadgeVariant {
  return match<IssueSeverity, BadgeVariant>(severity).with(ISev.CRITICAL, () => 'error').with(ISev.HIGH, () => 'errorMuted').with(ISev.MEDIUM, () => 'mutedWarning').with(ISev.LOW, () => 'neutralMuted').with(ISev.ENHANCED, () => 'primaryMuted').exhaustive();
}

/**
 * IssuePriority → Badge のバリアント変換。
 * URGENT は強警告、LOW は中立。Ticket の P0..P3 と段階を揃える。
 */
export function getIssuePriorityBadgeVariant(priority: IssuePriority): BadgeVariant {
  return match<IssuePriority, BadgeVariant>(priority).with(IPri.URGENT, () => 'errorMuted').with(IPri.HIGH, () => 'mutedWarning').with(IPri.MEDIUM, () => 'primaryMuted').with(IPri.LOW, () => 'neutralMuted').exhaustive();
}
export const convertIssueItemToTableRow = (item: IssueListItem): IssueTableRowData => {
  return {
    [IssueTableColumns.ISSUE]: {
      id: item.id,
      name: item.name
    },
    [IssueTableColumns.RELEASE_AND_TARGET_NODES]: {
      // BE rename: Issue.release → Issue.production (内部 shape は同一)。
      // FE 側の column / type 名は release を維持して minimal change に留める。
      release: item.production,
      targetNodes: item.targetNodes
    },
    [IssueTableColumns.STATUS]: item.status,
    [IssueTableColumns.STATUS_LABEL]: undefined,
    [IssueTableColumns.SEVERITY]: item.severity,
    [IssueTableColumns.SEVERITY_LABEL]: undefined,
    [IssueTableColumns.ENVIRONMENT]: item.environment,
    [IssueTableColumns.DETECTION_PATH]: item.detectionPath,
    [IssueTableColumns.ASSIGNEES]: item.assignees,
    [IssueTableColumns.REPORTER]: item.reporter,
    [IssueTableColumns.CREATED_AT]: item.createdAt
  };
};